// Utility functions for processing task mentions (#task-iid) in text

/**
 * Extract task IIDs from text that match #task-iid pattern
 * @param {string} text - Text to parse
 * @returns {Array<number>} - Array of unique task IIDs found
 */
export function extractTaskMentions(text) {
  if (!text) return []

  const pattern = /#(\d+)/g
  const matches = text.matchAll(pattern)
  const taskIds = []

  for (const match of matches) {
    const taskId = parseInt(match[1], 10)
    if (taskId > 0 && !taskIds.includes(taskId)) {
      taskIds.push(taskId)
    }
  }

  return taskIds
}

/**
 * Replace task mentions (#task-iid) with HTML links
 * @param {string} text - Text to process
 * @param {Object} taskMap - Map of taskIid -> { iid, id, title, projectId }
 * @param {Function} buildTaskUrl - Function to build task URL: (taskIid, projectId) => string
 * @returns {string} - HTML with task mentions replaced by links
 */
export function replaceTaskMentions(text, taskMap = {}, buildTaskUrl = null) {
  if (!text) return ''

  // Pattern to match #task-iid (not inside code blocks or links)
  const pattern = /#(\d+)/g

  return text.replace(pattern, (match, taskIdStr) => {
    const taskId = parseInt(taskIdStr, 10)
    if (taskId <= 0) return match

    const task = taskMap[taskId]
    if (!task) return match // Keep original if task not found

    const taskIid = task.iid
    if (!taskIid) return match
    const url = buildTaskUrl ? buildTaskUrl(taskIid, task.projectId) : `#task-${taskIid}`
    const displayText = task.title ? `#${taskIid} ${escapeHtml(task.title)}` : `#${taskIid}`

    return `<a href="${escapeHtml(url)}" class="task-mention-link" data-task-id="${taskIid}" data-project-id="${task.projectId}">${displayText}</a>`
  })
}

function escapeHtml(text) {
  if (!text) return ''
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }
  return String(text).replace(/[&<>"']/g, m => map[m])
}
